import React, { useContext, useEffect, useState } from "react";
import { Redirect, useHistory, useParams } from "react-router-dom";
import { QuoteGroupContext } from "../../../providers/RootContextProvider";
import cx from "classnames";
import styles from "./QuoteTableSummaryPage.module.scss";
import Layout from "../../Layout/Layout";
import Icon from "../../../../common/components/Icon/Icon";
import HelpSection from "../../../../common/components/HelpSection/HelpSection";
import QuoteTableFullCard from "../QuoteTable/Full/QuoteTableFullCard";
import IQuoteCardResult from "../../../interfaces/results/IQuoteCardResult";
import CustomerServiceModal from "../../Modals/CustomerServiceModal/CustomerServiceModal";
import ApplyModal from "../../Modals/ApplyModal/ApplyModal";
import QuoteCallToAction from "../Components/QuoteCallToAction/QuoteCallToAction";
import { IClient } from "../../../../common/interfaces/IClient";
import QuoteTableSummarySkeletonLoader from "./QuoteTableSummarySkeletonLoader";
import useStaticDOM from "../../../../common/hooks/useStaticDOM";
import useCart from "../../../../common/hooks/useCart";
import QuoteLoaderWrapper from "../../QuoteLoaderWrapper/QuoteLoaderWrapper";
import useQuoteSections from "../../../hooks/useQuoteSections";

interface IURLParams {
    key: string;
}

// We don’t want the DOM of the app itself to be selectable
const siteHeaderSelector = `.js-siteheader`;

const QuoteTableSummaryPage: React.FC = () => {
    const siteHeader = useStaticDOM(siteHeaderSelector);

    useEffect(() => {
        // hide
        siteHeader.hideElement();

        // display again
        return () => siteHeader.showElement();
    }, []);

    // modals control
    const [isOpenCustomerServiceModal, setIsOpenCustomerServiceModal] = useState(false);
    const [isOpenApplyModal, setIsOpenApplyModal] = useState(false);

    const { selectors, actions } = useContext(QuoteGroupContext);

    const { key } = useParams<IURLParams>();

    const history = useHistory();

    const queryParams = new URLSearchParams(history.location.search);
    const bannerHeadingText = queryParams.get("highlight");

    const clients: IClient[] = selectors.getClients();

    const { globalState, createQuoteTableState } = useQuoteSections(
        selectors.getGroupId(),
        {
            isAdmin: selectors.isAdmin(),
            hasSelection: false
        }
    );

    const groupId = selectors.getGroupId();
    const allResults = selectors.getQuoteResults();

    if (!allResults || allResults.length === 0) return <Redirect to="/results" />;

    const card: IQuoteCardResult = allResults.find(
        (result) => result.cardKey.toString() === key
    );

    const quoteState = createQuoteTableState(card, globalState);

    quoteState.isHighlighted = !!bannerHeadingText;

    const isLoadingQuotes = selectors.isLoadingQuoteResult();

    const { generateApplicationURL } = useCart();

    const applicationURL = generateApplicationURL(groupId, card);

    return (
        <QuoteLoaderWrapper>
            <Layout>
                <React.Fragment>
                    <CustomerServiceModal
                        results={allResults}
                        isOpen={isOpenCustomerServiceModal}
                        onClose={(): void => {
                            setIsOpenCustomerServiceModal(false);
                        }}
                    />
                    <ApplyModal
                        isOpen={isOpenApplyModal}
                        onClose={() => {
                            setIsOpenApplyModal(false);
                        }}
                        providerId={card?.result.providerId}
                        clients={clients}
                        settings={selectors.getAllSettings()}
                        benefits={card.result.benefits}
                        url={applicationURL}
                        handleClickStart={() => {
                            setIsOpenApplyModal(false);
                        }}
                    />
                    <div className={styles["summary-body"]}>
                        <div
                            className={cx(
                                styles["summary-header"],
                                "layout-flex-row",
                                "gap-m"
                            )}>
                            <a
                                onClick={() => {
                                    history.push(`/results`);
                                }}
                                className="layout-flex-row--centered text-font-size-m">
                                <Icon
                                    id="ChevronBack"
                                    className={cx("space-s", styles["icon"])}
                                />
                                Back
                            </a>

                            <QuoteCallToAction
                                groupId={groupId}
                                isDisabled={
                                    quoteState.hasBlockingError ||
                                    !quoteState.isOverMinimumPremium ||
                                    isLoadingQuotes
                                }
                                url={applicationURL}
                                className={styles["cta"]}
                                premium={card.result.totalPremium}
                                card={card}
                            />
                        </div>
                        {isLoadingQuotes ? (
                            <div className={cx("layout-flex-row--justify-centered")}>
                                <QuoteTableSummarySkeletonLoader
                                    className={cx("layout-flex-s")}
                                    isHighlighted={!!bannerHeadingText}
                                />
                            </div>
                        ) : (
                            <div
                                className={cx(
                                    "layout-flex-column--centered",
                                    styles["summary-card-block"]
                                )}>
                                <QuoteTableFullCard
                                    groupId={groupId}
                                    bannerHeadingText={bannerHeadingText}
                                    clients={clients}
                                    handlePaymentFrequencyChange={
                                        actions.setPaymentFrequency
                                    }
                                    handleSelectProduct={() => setIsOpenApplyModal(true)}
                                    handleClickCustomerServiceRating={() =>
                                        setIsOpenCustomerServiceModal(true)
                                    }
                                    card={card}
                                    quoteState={quoteState}
                                    className="gap-ml"
                                />
                            </div>
                        )}
                        <HelpSection
                            groupId={groupId}
                            contactDetails={selectors.getContactDetails()}
                            caseId={0}
                        />
                    </div>
                </React.Fragment>
            </Layout>
        </QuoteLoaderWrapper>
    );
};

export default QuoteTableSummaryPage;
